import classNames from 'classnames'
import { FormikErrors, FormikProps, getIn, withFormik } from 'formik'
import { t } from 'i18next'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { Form } from '@lib/formik'

import { Body1, Heading6 } from '@ui/Typography'
import { Button } from '@ui/components/Button'
import { Checkbox } from '@ui/components/Checkbox'
import { DocumentFileUpload } from '@ui/components/FileUpload/DocumentFileUpload'
import { FileData } from '@ui/components/FileUpload/types'
import { Radio } from '@ui/components/Radio'

import {
  useCategorySizesReadContext,
  useCategorySizesWriteContext,
} from '@utils/CategorySizesContext'
import { isZero } from '@utils/validations'

import { AccountDetailedDto } from '@model/AccountDetailedDto'
import { TestAnswerDto, TestAnswersDto } from '@model/ClientTestAnswerDto'
import { ClientTestDto } from '@model/ClientTestDto'
import { DocumentCategoryType } from '@model/DocumentCategories'
import { FreeTextAnswer } from '@model/TestSectionsDto'

import { Box } from '@shared/components/Box'

import styles from './ExperiencedClassificationTestForm.module.scss'

export interface ExperiencedClassificationTestValues {
  freeAnswers: FreeTextAnswer[]
  selectedAnswers: { question: string; answer: string }[]

  tradeSizeProofs: FileData[]
  knowledgeProofs: FileData[]

  classification_question_4: string
  classification_question_5: string
  classification_question_6: string

  classification_question_7: string
  classification_question_8: string
  classification_question_9: string

  classification_declaration_4: boolean
  classification_declaration_5: boolean
  classification_declaration_6: boolean
}

interface OuterProps {
  classificationName: string | undefined
  testData: ClientTestDto

  testAnswers: TestAnswersDto | undefined

  isFormEnabled: boolean

  handleFormSubmit: (values: ExperiencedClassificationTestValues) => void

  account: AccountDetailedDto | undefined
}

const ExperiencedClassificationTestFormUI: React.FC<
  FormikProps<ExperiencedClassificationTestValues> & OuterProps
> = (props) => {
  const {
    handleSubmit,
    classificationName,
    testData,
    setFieldValue,
    setFieldTouched,
    errors,
    values,
    isFormEnabled,
    isValid,
    testAnswers,
    isSubmitting,
    setSubmitting,
  } = props
  const {
    selectedAnswers,
    freeAnswers,
    classification_question_4,
    classification_question_5,
    classification_question_6,
    classification_question_7,
    classification_question_8,
    classification_question_9,
  } = values

  const { t } = useTranslation()
  const { categorySizes } = useCategorySizesReadContext()
  const { refreshCategorySizes } = useCategorySizesWriteContext()

  useEffect(() => {
    refreshCategorySizes()
  }, [])

  const handleSubmitForm = (event: React.FormEvent<HTMLFormElement>) => {
    setSubmitting(true)
    event.preventDefault()
    handleSubmit()
  }

  const canSubmit = () => {
    return (
      hasAffirmativeAnswer(
        [classification_question_4, classification_question_5, classification_question_6],
        1
      ) &&
      hasAffirmativeAnswer(
        [classification_question_5, classification_question_6, classification_question_7],
        1
      )
    )
  }

  const hasAffirmativeAnswer = (answers: string[], positiveAnswerCount: number) => {
    return answers.filter((answer) => answer === '1').length >= positiveAnswerCount
  }
  const handleKnowledgeProofOnChange = (files: FileData[]) => {
    setFieldValue('knowledgeProofs', [...files])
  }

  const handleTradeSizeProofOnChange = (files: FileData[]) => {
    setFieldValue('tradeSizeProofs', [...files])
  }

  return (
    <Form onSubmit={handleSubmitForm}>
      <Box bg='surface-white-smoke-0' radius='x16' p='x24' my='x8'>
        <Body1 isParagraph medium>
          <Body1 color='text-low-em'>{t('Profile.Current classification status:')}</Body1>{' '}
          <Body1 color='text-success-high-em'>{classificationName}</Body1>
        </Body1>
      </Box>
      <div className={styles.card}>
        <Heading6 medium>
          {t('Profile.Application Form to be Classified as a Experienced Retail Client')}
        </Heading6>
        <Body1 isParagraph>
          {t(
            'Profile.Please fill in the below form if you wish to apply to be classified as an Experienced Retail Client. You are responsible for keeping Tickmill informed about any change that could affect your current categorisation.'
          )}
        </Body1>
        <Body1 isParagraph>
          {t(
            'Profile.To qualify as an Experienced Retail Client you have to demonstrate sufficient CFDs trading experience and appropriate knowledge of derivatives, including CFDs.'
          )}
        </Body1>
        <Body1 isParagraph>
          {t('Profile.What will change if I am classified as an Experienced Retail Client?')}
        </Body1>
        <Body1 isParagraph>
          {t(
            'Profile.Should you possess the necessary criteria to be classified as an Experienced Retail Client'
          )}
        </Body1>
      </div>
      {testData.sections.map((section, sectionIndex) => {
        if (sectionIndex === 2) {
          return (
            <div className='mt-5 mb-5' key={sectionIndex}>
              {!isFormEnabled && (
                <div className='mt-5 pl-4 mb-5'>
                  {
                    <Body1 isParagraph>{`Uploaded documents: ${
                      testAnswers?.documents.map((doc) => doc.name).join(', ') || ''
                    }`}</Body1>
                  }
                </div>
              )}
              {section.questions.map((question, questionIndex) => {
                const fieldName = question.widget.name
                const error = getIn(errors, fieldName)
                return (
                  <React.Fragment key={questionIndex}>
                    <Checkbox
                      disabled={!isFormEnabled}
                      name={fieldName}
                      error={error}
                      onChange={async (checked: boolean) => {
                        const filteredArray = freeAnswers.filter(
                          (x) => x.testQuestionId !== question.question.id
                        )
                        const newValue = {
                          testQuestionId: question.question.id,
                          answer: checked ? '1' : '0',
                        }
                        setFieldValue('freeAnswers', [...filteredArray, newValue])
                        await setFieldValue(fieldName, checked)
                        setFieldTouched(fieldName)
                      }}
                      checked={getIn(values, fieldName)}
                    >
                      {question.question.name}
                    </Checkbox>
                  </React.Fragment>
                )
              })}
            </div>
          )
        }
        return (
          <div className={styles.card} key={sectionIndex}>
            <Body1 isParagraph medium>{`${sectionIndex + 1}. ${section.name}`}</Body1>
            <div className={styles.overviewWrapper}>
              {section.questions.map((question) => {
                return (
                  <div className={classNames(styles.overview)} key={question.question.id}>
                    <Body1 isParagraph>{`${question.question.name}`}</Body1>

                    {question.answers.map((answer) => (
                      <Radio
                        name={question.widget.name}
                        value={answer.code}
                        key={answer.code}
                        onChange={async () => {
                          const filteredArray = selectedAnswers.filter(
                            (x) => x.question !== question.question.id
                          )
                          const newValue = {
                            question: question.question.id,
                            answer: answer.id,
                          }
                          await setFieldValue('selectedAnswers', [...filteredArray, newValue])
                          setFieldValue(question.widget.name, answer.code)
                        }}
                      >
                        {answer.name}
                      </Radio>
                    ))}
                  </div>
                )
              })}
            </div>
            {isFormEnabled && (
              <div
                className={classNames({
                  'is-hidden': isZero(sectionIndex)
                    ? !hasAffirmativeAnswer(
                        [
                          classification_question_4,
                          classification_question_5,
                          classification_question_6,
                        ],
                        1
                      )
                    : !hasAffirmativeAnswer(
                        [
                          classification_question_7,
                          classification_question_8,
                          classification_question_9,
                        ],
                        1
                      ),
                })}
              >
                <div className='mt-4'>
                  <DocumentFileUpload
                    categorySizes={categorySizes}
                    documentCategory={DocumentCategoryType.Additional}
                    onUpload={
                      isZero(sectionIndex)
                        ? handleTradeSizeProofOnChange
                        : handleKnowledgeProofOnChange
                    }
                    multiple
                  />
                </div>
              </div>
            )}
          </div>
        )
      })}
      <div className={styles.buttonWrapper}>
        {isFormEnabled && (
          <Button.Primary
            size='lg'
            type='submit'
            disabled={!canSubmit() || !isValid || isSubmitting}
          >
            {t('Confirm')}
          </Button.Primary>
        )}
      </div>
    </Form>
  )
}

export const ExperiencedClassificationTestForm = withFormik<
  OuterProps,
  ExperiencedClassificationTestValues
>({
  mapPropsToValues: ({ testAnswers }) => {
    const answers: TestAnswerDto[] =
      testAnswers?.sections.map((section) => section.answers).flat() || []
    return {
      selectedAnswers: [],
      freeAnswers: [],
      classification_question_4:
        (!testAnswers?.allowRetake &&
          answers.find((answer) => answer.question.widgetName === 'classification_question_4')
            ?.answer.code) ||
        '',
      classification_question_5:
        (!testAnswers?.allowRetake &&
          answers.find((answer) => answer.question.widgetName === 'classification_question_5')
            ?.answer.code) ||
        '',
      classification_question_6:
        (!testAnswers?.allowRetake &&
          answers.find((answer) => answer.question.widgetName === 'classification_question_6')
            ?.answer.code) ||
        '',

      classification_question_7:
        (!testAnswers?.allowRetake &&
          answers.find((answer) => answer.question.widgetName === 'classification_question_7')
            ?.answer.code) ||
        '',
      classification_question_8:
        (!testAnswers?.allowRetake &&
          answers.find((answer) => answer.question.widgetName === 'classification_question_8')
            ?.answer.code) ||
        '',
      classification_question_9:
        (!testAnswers?.allowRetake &&
          answers.find((answer) => answer.question.widgetName === 'classification_question_9')
            ?.answer.code) ||
        '',

      classification_declaration_4:
        !testAnswers?.allowRetake &&
        answers.find((answer) => answer.question.widgetName === 'classification_declaration_4')
          ?.answer.name === '1',
      classification_declaration_5:
        !testAnswers?.allowRetake &&
        answers.find((answer) => answer.question.widgetName === 'classification_declaration_5')
          ?.answer.name === '1',
      classification_declaration_6:
        !testAnswers?.allowRetake &&
        answers.find((answer) => answer.question.widgetName === 'classification_declaration_6')
          ?.answer.name === '1',

      knowledgeProofs: [],
      tradeSizeProofs: [],
    }
  },
  handleSubmit: (values, { props, setSubmitting }) => {
    try {
      props.handleFormSubmit(values)
    } catch {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<ExperiencedClassificationTestValues> = {}
    if (!values.classification_declaration_4) {
      errors.classification_declaration_4 = t('Validation.Required')
    }
    if (!values.classification_declaration_5) {
      errors.classification_declaration_5 = t('Validation.Required')
    }
    if (!values.classification_declaration_6) {
      errors.classification_declaration_6 = t('Validation.Required')
    }
    if (!values.classification_question_4) {
      errors.classification_question_4 = t('Validation.Required')
    }
    if (!values.classification_question_5) {
      errors.classification_question_5 = t('Validation.Required')
    }
    if (!values.classification_question_6) {
      errors.classification_question_6 = t('Validation.Required')
    }
    if (!values.classification_question_7) {
      errors.classification_question_7 = t('Validation.Required')
    }
    if (!values.classification_question_8) {
      errors.classification_question_8 = t('Validation.Required')
    }
    if (!values.classification_question_9) {
      errors.classification_question_9 = t('Validation.Required')
    }
    if (!values.knowledgeProofs.filter((x) => x.fileName).length) {
      errors.knowledgeProofs = t('Validation.Required')
    }
    if (!values.tradeSizeProofs.filter((x) => x.fileName).length) {
      errors.tradeSizeProofs = t('Validation.Required')
    }

    return errors
  },

  enableReinitialize: true,
  isInitialValid: false,
})(ExperiencedClassificationTestFormUI)
